/**
 * AuthImage — fetch an image with the same auth headers our axios
 * client uses, then render the resulting Blob as an object URL.
 *
 * Why not <img src> directly? Browsers don't send custom headers
 * (X-User-Email, Authorization) on plain <img> requests; only cookies.
 * For localStorage-only sessions or external bearer-token sessions
 * we have to fetch in JavaScript so we can attach the headers.
 *
 * We use native fetch (not axios) here because axios' XHR adapter
 * trips on `responseText` reads when responseType='blob' fails, which
 * floods the browser console for transient errors.
 */
import { useEffect, useState } from "react";
import { getCachedImage, setCachedImage, blobToDataUrl } from "@/lib/convMessageCache";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

function buildAuthHeaders(path) {
  const headers = {};
  let hasUser = false;
  try {
    const saved = localStorage.getItem("usair_user");
    if (saved) {
      const u = JSON.parse(saved);
      if (u?.email) { headers["X-User-Email"] = u.email; hasUser = true; }
    }
  } catch { /* ignore */ }
  // Only fall back to the conv bearer session when there is no
  // portal user — otherwise a stale token left over from an iter124
  // OTP-redeem test session would shadow the real portal auth.
  try {
    const sess = localStorage.getItem("usair_conv_session");
    if (!hasUser && sess && path.startsWith("/conv")) {
      headers["Authorization"] = `Bearer ${sess}`;
    }
  } catch { /* ignore */ }
  return headers;
}

export default function AuthImage({ src, alt = "", className = "", testId, onLoad, onError }) {
  const [blobUrl, setBlobUrl] = useState("");
  const [failed, setFailed] = useState("");

  useEffect(() => {
    if (!src) { setBlobUrl(""); setFailed(""); return undefined; }
    let cancelled = false;
    let url = "";
    setFailed("");
    // iter134.10 — local in-memory references (used by the optimistic
    // upload path while the file is still being POSTed) are valid
    // ``<img src>`` values themselves; render them straight without
    // fetching. ``blob:`` URLs come from ``URL.createObjectURL(file)``;
    // ``data:`` URLs come from the localStorage image cache.
    if (src.startsWith("blob:") || src.startsWith("data:")) {
      setBlobUrl(src);
      if (onLoad) onLoad();
      return undefined;
    }
    // Defensive — only fetch URLs that look like a server path or
    // a full URL. Anything else is junk content (e.g. a content_type
    // mis-tag in fixture data) and we surface a clear error.
    if (!src.startsWith("/") && !src.startsWith("http")) {
      setFailed(`Invalid image reference: ${src.slice(0, 40)}`);
      if (onError) onError();
      return undefined;
    }
    // iter134.4 — try the localStorage image cache first. A hit
    // paints in the same React tick with zero network bytes; misses
    // fall through to fetch-and-store below. Data URLs are valid
    // ``<img src>`` values so we can use them directly.
    const cached = getCachedImage(src);
    if (cached) {
      setBlobUrl(cached);
      if (onLoad) onLoad();
      return undefined;
    }
    const apiPath = src.startsWith("/api/") ? src.slice(4) : src;
    const full = src.startsWith("http") ? src : `${BACKEND_URL}/api${apiPath}`;
    fetch(full, {
      credentials: "include",
      headers: buildAuthHeaders(apiPath),
    })
      .then((resp) => {
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        return resp.blob();
      })
      .then(async (blob) => {
        if (cancelled) return;
        url = URL.createObjectURL(blob);
        setBlobUrl(url);
        if (onLoad) onLoad();
        // Best-effort write to the image cache. Done after the user
        // sees the image so the encoding cost doesn't block paint.
        // Skipped for blobs larger than the per-item cap inside
        // ``setCachedImage`` so we don't blow the localStorage quota.
        try {
          const dataUrl = await blobToDataUrl(blob);
          setCachedImage(src, dataUrl);
        } catch { /* cache is best-effort */ }
      })
      .catch((err) => {
        if (cancelled) return;
        setFailed(String(err?.message || "Could not load image"));
        if (onError) onError();
      });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [src, onLoad, onError]);

  if (failed) {
    return (
      <div
        data-testid={testId}
        className={`flex flex-col items-center justify-center bg-slate-100 border border-slate-200 text-slate-500 text-[11px] gap-1 px-3 py-4 rounded-md ${className}`}
        title={failed}
      >
        <span className="text-slate-400 text-base">🖼️</span>
        <span className="italic">{failed.length > 60 ? failed.slice(0, 57) + "…" : failed}</span>
      </div>
    );
  }
  if (!blobUrl) {
    return (
      <div
        data-testid={testId}
        className={`flex items-center justify-center bg-slate-50 border border-slate-100 text-slate-300 text-[10px] ${className}`}
      >
        Loading…
      </div>
    );
  }
  return (
    <img
      src={blobUrl}
      alt={alt}
      data-testid={testId}
      className={className}
      loading="lazy"
    />
  );
}
